import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { Add, Delete, ChatBubbleOutline } from "@mui/icons-material";
import type { ConversationSummary } from "@/types";

interface ConversationListPanelProps {
  conversations: ConversationSummary[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  loading?: boolean;
}

function groupByDay(
  conversations: ConversationSummary[],
): { label: string; rows: ConversationSummary[] }[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const groups: Record<string, ConversationSummary[]> = {
    Today: [],
    Yesterday: [],
    Earlier: [],
  };
  for (const c of conversations) {
    const t = new Date(c.last_message_at);
    if (t >= today) groups.Today.push(c);
    else if (t >= yesterday) groups.Yesterday.push(c);
    else groups.Earlier.push(c);
  }
  return Object.entries(groups)
    .filter(([, rows]) => rows.length > 0)
    .map(([label, rows]) => ({ label, rows }));
}

export default function ConversationListPanel({
  conversations,
  selectedId,
  onSelect,
  onNew,
  onDelete,
  loading,
}: ConversationListPanelProps) {
  const groups = groupByDay(conversations);

  return (
    <Box
      sx={{
        width: 280,
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<Add />}
          onClick={onNew}
          disabled={loading}
        >
          New conversation
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {conversations.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, fontStyle: "italic" }}>
            No conversations yet — start by clicking "New conversation".
          </Typography>
        )}
        {groups.map((group) => (
          <Box key={group.label}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                px: 2,
                pt: 1.5,
                pb: 0.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 1,
                display: "block",
              }}
            >
              {group.label}
            </Typography>
            <List dense disablePadding>
              {group.rows.map((c) => (
                <ListItemButton
                  key={c.id}
                  selected={c.id === selectedId}
                  onClick={() => onSelect(c.id)}
                  sx={{ pr: 1 }}
                >
                  <ChatBubbleOutline fontSize="small" sx={{ mr: 1.5, color: "action.active" }} />
                  <ListItemText
                    primary={c.title || "Untitled"}
                    primaryTypographyProps={{ noWrap: true, variant: "body2" }}
                  />
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm("Delete this conversation?")) onDelete(c.id);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
